// Quick layout: turns an ordered list of fields + spacing into the same field objects the
// drag-and-drop designer produces (inches, centred, stacked top to bottom).

export const QUICK_ID_PREFIX = 'quick_';
const LINE_HEIGHT = 1.25;
const TEXT_PADDING_IN = 0.04;

const round = (value) => Math.round(value * 100) / 100;

export const textRowHeight = (fontSize, lines = 1) =>
  round(((fontSize || 14) / 72) * LINE_HEIGHT * Math.max(1, lines) + TEXT_PADDING_IN);

export function newRow(type = 'text', overrides = {}) {
  const base = type === 'text'
    ? { type, key: '', label: '', fontSize: 14, bold: false, textCase: 'auto', lines: 1 }
    : { type, key: type === 'qrCode' ? 'qrCodeBase64' : '', label: type === 'qrCode' ? 'QR Code' : 'Photo', size: 1 };
  return { ...base, ...overrides };
}

/**
 * rows: [{ type: 'text'|'qrCode'|'image', key, label, fontSize, bold, textCase, lines, size }]
 * Returns fields positioned from `top`, each separated by `gap`, centred within `sidePadding`.
 */
export function buildStackedFields({ paper, top = 1, gap = 0.1, sidePadding = 0.2, rows = [] }) {
  const width = Math.max(0.5, paper.width - 2 * sidePadding);
  let y = top;
  return rows
    .filter((row) => row && String(row.key || '').trim())
    .map((row, index) => {
      const key = row.key.trim();
      const id = `${QUICK_ID_PREFIX}${index}_${key.replace(/[^a-zA-Z0-9]/g, '')}`;
      let field;
      if (row.type === 'text') {
        const height = textRowHeight(row.fontSize, row.lines);
        field = {
          id,
          key,
          label: (row.label || key).trim(),
          type: 'text',
          x: round(sidePadding),
          y: round(y),
          width: round(width),
          height,
          visible: true,
          style: {
            fontSize: row.fontSize || 14,
            fontWeight: row.bold ? 'bold' : 'normal',
            color: '#000000',
            fontFamily: row.fontFamily || 'Arial, sans-serif',
            textAlign: 'center',
            rotation: 0,
            textCase: row.textCase || 'auto',
          },
          ...(row.properties ? { properties: row.properties } : {}),
        };
      } else {
        const size = Math.min(row.size || 1, width);
        field = {
          id,
          key,
          label: (row.label || key).trim(),
          type: row.type,
          x: round((paper.width - size) / 2),
          y: round(y),
          width: round(size),
          height: round(size),
          visible: true,
          style: { rotation: 0 },
          ...(row.properties ? { properties: row.properties } : {}),
        };
      }
      y += field.height + gap;
      return field;
    });
}

/** Best-effort reverse of buildStackedFields so an existing layout can be tweaked here. */
export function rowsFromFields(fields = []) {
  return [...fields]
    .filter((field) => field && field.visible !== false)
    .sort((a, b) => (a.y || 0) - (b.y || 0))
    .map((field) => (field.type === 'text'
      ? newRow('text', {
        key: field.key,
        label: field.label,
        fontSize: field.style?.fontSize || 14,
        bold: ['bold', '600', '700', 700, 600].includes(field.style?.fontWeight),
        textCase: field.style?.textCase || 'auto',
        fontFamily: field.style?.fontFamily,
        lines: Math.max(1, Math.round((field.height - TEXT_PADDING_IN) / (((field.style?.fontSize || 14) / 72) * LINE_HEIGHT))),
        properties: field.properties,
      })
      : newRow(field.type, {
        key: field.key,
        label: field.label,
        size: Math.min(field.width || 1, field.height || 1),
        properties: field.properties,
      })));
}

/** Spacing that reproduces an existing layout (first field's top, average gap, side padding). */
export function spacingFromFields(fields = [], paper) {
  const sorted = [...fields].filter((f) => f && f.visible !== false).sort((a, b) => a.y - b.y);
  if (!sorted.length) return { top: round(paper.height * 0.35), gap: 0.1, sidePadding: 0.2 };
  const gaps = sorted.slice(1).map((f, i) => f.y - (sorted[i].y + sorted[i].height)).filter((g) => g >= 0);
  const text = sorted.find((f) => f.type === 'text');
  return {
    top: round(sorted[0].y),
    gap: gaps.length ? round(gaps.reduce((a, b) => a + b, 0) / gaps.length) : 0.1,
    sidePadding: text ? round(Math.max(0, text.x)) : 0.2,
  };
}
